import { Currency, MoneyRecord } from '@navio/nsdk';

import { formatCompactNotation } from '../numbers/numbers';
import { MISSING_VALUE } from '../string/string';

export { Currency } from '@navio/nsdk';

/** The company's own currency, for a caller that has none to pass — not a formatting fallback. */
export const DEFAULT_CURRENCY = Currency.NOK;

/** Convert major units (e.g. kr) to minor units (e.g. øre) — rounds to avoid float drift */
export const toMinor = (major: number): number => Math.round(major * 100);

/** Convert minor units (e.g. øre) back to major units (e.g. kr) */
export const toMajor = (minor: number): number => minor / 100;

const FORMATTER_STYLE = 'currency';

enum Locales {
  // nb-NO, not the umbrella no-NO: Android's Intl does not resolve `no` and falls back to English ("NOK 1,234.00").
  NOK = 'nb-NO',
  SEK = 'sv-SE',
  EUR = 'de-DE',
  CAD = 'en-CA',
  USD = 'en-US'
}

function getCurrencyLocales(currency: Currency): string {
  switch (currency) {
    case Currency.NOK:
      return Locales.NOK;
    case Currency.SEK:
      return Locales.SEK;
    case Currency.EUR:
      return Locales.EUR;
    case Currency.CAD:
      return Locales.CAD;
    case Currency.USD:
      return Locales.USD;
    default:
      return Locales.NOK;
  }
}

export type MoneyWithOptionalCurrency = {
  amount: number;
  currency?: Currency;
};

export const formatCurrencyAmount = ({
  money,
  decimals = 2
}: {
  money?: MoneyWithOptionalCurrency | MoneyRecord | null;
  decimals?: 0 | 2;
}) => {
  if (money == null || typeof money.amount !== 'number') {
    return MISSING_VALUE;
  }
  const currency = money.currency ?? Currency.NOK;
  const currencyLocales = getCurrencyLocales(currency);

  const currencyFormatter = new Intl.NumberFormat(currencyLocales, {
    style: FORMATTER_STYLE,
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });

  return currencyFormatter.format(money.amount);
};

/** The sign {@link formatCurrencyAmount} writes for the currency: "kr" for NOK, "€" for EUR. */
export const getCurrencySymbol = (currency: Currency): string =>
  // A formatted zero minus its number, not `formatToParts`: Hermes (the worker app) does not implement it.
  new Intl.NumberFormat(getCurrencyLocales(currency), { style: FORMATTER_STYLE, currency })
    .format(0)
    .replace(/[\d.,\s]/g, '') || currency;

/** Axis-label money, e.g. "12K kr" -- same currency locale as {@link formatCurrencyAmount}. */
export const formatCompactCurrencyAmount = ({ money }: { money: MoneyWithOptionalCurrency | MoneyRecord }) => {
  const currency = money.currency ?? Currency.NOK;
  return formatCompactNotation(
    new Intl.NumberFormat(getCurrencyLocales(currency), {
      style: FORMATTER_STYLE,
      currency,
      notation: 'compact',
      maximumFractionDigits: 1
    }),
    money.amount
  );
};

/**
 * Format a discount as a negative amount. Discount amounts from the API are
 * positive values — the abs guard makes the sign convention impossible to get wrong.
 */
export const formatDiscountAmount = (money?: MoneyWithOptionalCurrency | MoneyRecord | null): string => {
  if (money == null || typeof money.amount !== 'number') {
    return MISSING_VALUE;
  }
  return formatCurrencyAmount({ money: { amount: -Math.abs(money.amount), currency: money.currency } });
};

const greatestCommonDivisor = (a: number, b: number): number => (b === 0 ? a : greatestCommonDivisor(b, a % b));

/**
 * Snap a gross amount to the nearest one whose implied net is a whole number of
 * minor units and whose `net * vatFactor` is decimal-free — so the price survives
 * a round-trip through minor-unit storage.
 *
 * With VAT in basis points, net must land on a multiple of
 * `10_000 / gcd(10_000 + vatBasisPoints, 10_000)` minor units: every 4 at 25% VAT,
 * every 25 at 12%, every 8 at 12.5%, every 1 at 0%.
 */
export const snapGrossPriceToStorableNet = ({
  grossAmount,
  vatPercentage
}: {
  grossAmount: number;
  vatPercentage: number;
}): number => {
  const vatNumerator = 10_000 + Math.round(vatPercentage * 100);
  const netStepMinor = 10_000 / greatestCommonDivisor(vatNumerator, 10_000);

  const netMinor = Math.round((toMinor(grossAmount) * 10_000) / vatNumerator / netStepMinor) * netStepMinor;

  return toMajor(Math.round((netMinor * vatNumerator) / 10_000));
};
